import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  SquaresFour,
  MapTrifold,
  PresentationChart,
  ClipboardText,
  ChartLineUp,
  DropHalf,
  Leaf,
  Boat,
  Notebook,
  Compass,
  Lifebuoy,
  GearSix,
  ClockCounterClockwise,
  ShieldWarning,
  SignOut,
  Circle,
  MagnifyingGlass,
  Database,
  Blueprint,
  Wrench,
  ShoppingCart,
  FirstAidKit,
  UsersThree,
  Anchor,
  CalendarBlank,
  ListChecks,
  Clipboard,
  WarningCircle,
  ChatCircleDots,
  Package,
  SealCheck,
  CurrencyDollar,
  ListBullets,
  ShieldCheck,
  CaretDown,
  Sparkle,
} from "@phosphor-icons/react";
import constant from "../../constant";
import {
  ISHIP_MODULES,
  modulePath,
  sidebarSectionModules,
  sidebarSecondaryModules,
} from "../../config/ishipModules";
import { v2_fetch_explore_catalog } from "../../actions/v2/explore";
import { ENTITLEMENTS_EVENT } from "../../utils/moduleEntitlements";
import { MODERN_ICON } from "../../constants/modernIcons";
import { useCurrentWorkingVessel } from "../../customHooks/useCurrentWorkingVessel";
import { useCan } from "../../hooks/useCan";
import { usePermissions } from "../../context/PermissionContext";
import { useUserDetails } from "../../hooks/useUserDetails";
import { getDesignationName, isMasterRank, isVendorRank, isShipyardRank } from "../../utils/marpolRoleAccess";

const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";
const RAIL_ICON = MODERN_ICON.rail;

const MODULE_ITEM_ICONS = {
  dashboard: Clipboard,
  overview: ChartLineUp,
  "my-actions": ListChecks,
  "fleet-status": ChartLineUp,
  alerts: WarningCircle,
  reports: ChartLineUp,
  "crew-operations": UsersThree,
  "weekly-task-management": CalendarBlank,
  "daily-tasks-register": ListChecks,
  "vessel-inspection": ClipboardText,
  maintenance: Wrench,
  pms: Wrench,
  plans: Wrench,
  "work-orders": Clipboard,
  equipment: Wrench,
  verification: SealCheck,
  "reports-analytics": ChartLineUp,
  defects: WarningCircle,
  "defect-list": WarningCircle,
  list: WarningCircle,
  safety: SealCheck,
  certificates: SealCheck,
  history: ListBullets,
  inventory: Package,
  "inventory-procurement": Package,
  stock: Package,
  reservations: Package,
  "material-readiness": Package,
  procurement: ShoppingCart,
  "fleet-overview": ChartLineUp,
  requisition: ShoppingCart,
  pipeline: ChatCircleDots,
  rfq: ChatCircleDots,
  quotations: ChatCircleDots,
  "rfq-quotes": ChatCircleDots,
  "purchase-orders": Package,
  "goods-receipt": Package,
  vendors: ShoppingCart,
  "delivery-note": Package,
  hseq: FirstAidKit,
  incidents: FirstAidKit,
  "hseq-inspections": SealCheck,
  "safety-drills": Lifebuoy,
  sms: ShieldCheck,
  library: ShieldCheck,
  drydock: Anchor,
  "drydock-repair-list": ListBullets,
  "drydock-cost-tracking": CurrencyDollar,
};

const RAIL_GROUPS = [
  {
    id: "dashboard",
    number: "01",
    label: "Dashboard",
    names: ["Dashboard", "Fleet Map", "Voyage"],
    displayNames: { Dashboard: "Fleet Overview" },
  },
  {
    id: "environment",
    number: "02",
    label: "Environment",
    names: ["Environment", "MARPOL"],
    displayNames: { Environment: "Efficiency Indicators" },
  },
  {
    id: "kpi",
    number: "03",
    label: "KPI",
    names: ["KPI"],
    displayNames: { KPI: "KPI Dashboard" },
  },
  {
    id: "intelligence",
    number: "04",
    label: "iShip Intelligence",
    names: ["iShip Intelligence"],
    displayNames: { "iShip Intelligence": "Ask fleet & documents" },
  },
  {
    id: "vessel-performance",
    number: "05",
    label: "Vessel performance",
    names: ["Vessel Performance"],
  },
  {
    id: "reporting",
    number: "06",
    label: "Reporting",
    names: ["Reports", "My Reports", "Reporting"],
    displayNames: {
      Reports: "Voyage Reports",
      Reporting: "Vessel Reporting",
    },
  },
  {
    id: "logs",
    number: "06",
    label: "Logs",
    names: ["Logs"],
    displayNames: { Logs: "Logbooks" },
  },
  {
    id: "operational-modules",
    names: ["Modules"],
    hiddenHeader: true,
  },
  {
    id: "settings",
    number: "12",
    label: "Settings & administration",
    names: [
      "Master Data",
      "Template Builders",
      "Explore",
      "Settings",
      "Activity & IP Log",
      "Support",
    ],
  },
];

const RAIL_TOOLTIPS = {
  "Activity & IP Log": "Audit Trail",
  "Master Data": "Fleet & people hub",
  "Template Builders": "Template hub",
  Settings: "Settings hub",
  Modules: "Operations modules",
  Explore: "Explore services",
};

const SIDEBAR_PERMISSION_RULES = {
  Dashboard: ["crew.dashboard", "owner.dashboard", "superadmin.dashboard"],
  "iShip Intelligence": ["crew.dashboard", "owner.dashboard", "superadmin.dashboard"],
  "Vessel Performance": ["crew.dashboard", "owner.dashboard", "superadmin.dashboard"],
  "Fleet Map": [
    "vessel.management",
    "vessel.group",
    "vessel.owner",
    "vessel.master-data",
  ],
  Reports: ["reports."],
  "My Reports": ["reports."],
  MARPOL: ["marpol."],
  Reporting: ["elog.", "reports."],
  Logs: ["elog.", "operations."],
  Voyage: ["reports.sosp", "reports.port", "reports.eosp"],
  "Master Data": [
    "masterdata.",
    "vessel.management",
    "vessel.group",
    "vessel.owner",
    "vessel.master-data",
  ],
  "Template Builders": [
    "templatebuilders.",
    "masterdata.compliance-templates",
    "operations.form-builder",
  ],
  Settings: ["settings."],
};

const SIDEBAR_ACCESS_AREA_RULES = {
  Dashboard: ["dashboard"],
  "Fleet Map": ["fleet"],
  Reports: ["reports"],
  "My Reports": ["reports"],
  Reporting: ["reports", "logbooks"],
  Voyage: ["reports", "logbooks"],
  Logs: ["logbooks"],
  MARPOL: ["marpol"],
  KPI: ["kpi"],
  "Vessel Performance": ["performance"],
  Environment: ["environment"],
  "Master Data": ["master-data"],
  "Template Builders": ["templates"],
  Modules: ["resources"],
  Explore: ["explore"],
  Settings: ["settings"],
  Support: ["support"],
  "Activity & IP Log": ["audit"],
  "Vendor Dashboard": ["resources"],
  "Shipyard Dashboard": ["resources"],
};

/** Flat submenu for collapsed flyout — primary sections + secondary leaves */
const buildModulesFlyoutSubmenu = (modules = ISHIP_MODULES) => {
  const primary = sidebarSectionModules(modules);
  const secondary = sidebarSecondaryModules(modules);
  return [
    ...primary.map((mod) => ({
      name: mod.name,
      url: modulePath(mod.id, mod.submodules?.[0]?.id),
      iconKey: mod.id,
    })),
    ...(secondary.length
      ? [
          { name: "More operations", sectionHeader: true },
          ...secondary.flatMap((mod) =>
            (mod.submodules || [])
              .filter((s) => !s.hiddenTab)
              .map((s) => ({
                name: `${mod.sidebarName || mod.name}: ${s.name}`,
                url: modulePath(mod.id, s.id),
                iconKey: s.id,
              }))
          ),
        ]
      : []),
  ];
};


const buildRailGroups = (items) => {
  const remaining = [...items];
  const groups = [];

  for (const group of RAIL_GROUPS) {
    const groupItems = group.names
      .map((name) => {
        const idx = remaining.findIndex((item) => item.name === name);
        if (idx === -1) return null;
        const item = remaining.splice(idx, 1)[0];
        return {
          ...item,
          displayName: group.displayNames?.[name] || item.name,
        };
      })
      .filter(Boolean);

    if (groupItems.length > 0) {
      const visibleNames = new Set(groupItems.map((item) => item.name));
      const label =
        group.id === "environment" &&
        !visibleNames.has("Environment") &&
        visibleNames.has("MARPOL")
          ? "MARPOL"
          : group.label;
      groups.push({
        id: group.id,
        number: group.number,
        label,
        hiddenHeader: group.hiddenHeader,
        items: groupItems,
      });
    }
  }

  if (remaining.length > 0) {
    groups.push({
      id: "other",
      number: "14",
      label: "Other",
      items: remaining,
    });
  }

  return groups;
};

const isSidebarCollapsed = () =>
  document.body.classList.contains("sidebar-collapsed") ||
  localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";

const isNarrowViewport = () => window.innerWidth <= 768;

const setSubmenuDrawerOpen = (open) => {
  const wrapper = document.getElementById("layout-wrapper");
  if (!wrapper) return;
  if (open) {
    wrapper.classList.add("active");
  } else {
    wrapper.classList.remove("active");
  }
};

const getStoredVesselId = () => {
  try {
    const saved = localStorage.getItem(constant.keys.selectedVessel);
    if (!saved) return null;
    const v = JSON.parse(saved);
    return v.value ?? v.id ?? null;
  } catch {
    return null;
  }
};

/**
 * ✅ Reusable Sidebar Component
 * @param {Array} sidebarOptions - Array of { name, url, icon }
 */

const CommonSidebar = () => {
  const { vessel } = useCurrentWorkingVessel();
  const { user } = useUserDetails();
  const {
    permissions,
    roleName,
    userRestricted,
    accessAreas,
    loading: permissionsLoading,
  } = usePermissions();
  const allowedAccessAreas = new Set(accessAreas || []);

  const adminPerm = useCan("superadmin.dashboard");
  const ownerPerm = useCan("owner.dashboard");
  const crewPerm = useCan("crew.dashboard");
  const nearMissPerm = useCan("safety.near-miss");
  const ssoPerm = useCan("settings.sso");
  const masterSignPerm = useCan("elog.sign.master", { bypassAdmin: false });
  const ceSignPerm = useCan("elog.sign.ce", { bypassAdmin: false });
  const oowSignPerm = useCan("elog.sign.oow", { bypassAdmin: false });
  const orb1Perm = useCan("marpol.orb1", { bypassAdmin: false });
  const orb2Perm = useCan("marpol.orb2", { bypassAdmin: false });
  const grbPerm = useCan("marpol.grb", { bypassAdmin: false });
  const bwrbPerm = useCan("marpol.bwrb", { bypassAdmin: false });
  const bfrbPerm = useCan("marpol.bfrb", { bypassAdmin: false });
  const annex4Perm = useCan("marpol.annex4", { bypassAdmin: false });
  const annex6Perm = useCan("marpol.annex6", { bypassAdmin: false });
  const isCrewProfile =
    !adminPerm.canView &&
    (crewPerm.canView || String(roleName || "").toLowerCase() === "crew");
  const isMasterProfile =
    !adminPerm.canView &&
    !ownerPerm.canView &&
    isMasterRank(getDesignationName(user));
  const isVendorProfile = isVendorRank(getDesignationName(user));
  const isShipyardProfile = isShipyardRank(getDesignationName(user));

  const location = useLocation();
  const navigate = useNavigate();

  const [activeMenu, setActiveMenu] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // 🔍 Added for search input
  const [filteredSubmenu, setFilteredSubmenu] = useState([]);
  const [openRightItem, setOpenRightItem] = useState(null);
  const [pendingFlyout, setPendingFlyout] = useState(null);
  const [entitledModules, setEntitledModules] = useState(ISHIP_MODULES);
  const [openNavigationGroup, setOpenNavigationGroup] = useState(null);
  const menuNavRef = React.useRef(null);

  const toggleNavigationGroup = (groupId) => {
    setOpenNavigationGroup((current) => current === groupId ? null : groupId);
  };

  useEffect(() => {
    const CACHE_KEY = "vi_explore_catalog_v8_defect_list";
    const CACHE_TTL_MS = 5 * 60_000;

    const applyCatalog = (modules) => {
      if (!modules?.length) return;
      const byId = Object.fromEntries(modules.map((m) => [m.id, m]));
      const next = ISHIP_MODULES.map((mod) => {
        if (mod.sidebarHidden) return null;
        const cat = byId[mod.id];
        // Primary three-section modules always stay available from local config
        // so a stale explore catalog cannot strip their internal tabs.
        if (mod.sidebarPrimary) return mod;
        if (!cat) return mod;
        if (cat.status !== "active") return null;
        const allowedSubs = new Set(
          (cat.submodules || [])
            .filter((s) => (s.status || cat.status) === "active")
            .map((s) => s.id)
        );
        const submodules = (mod.submodules || []).filter((s) =>
          allowedSubs.size ? allowedSubs.has(s.id) : true
        );
        if (!submodules.length) return null;
        return { ...mod, submodules };
      }).filter(Boolean);
      setEntitledModules(next);
    };

    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.at && Date.now() - parsed.at < CACHE_TTL_MS && parsed.modules) {
          applyCatalog(parsed.modules);
        }
      }
    } catch {
      /* ignore */
    }

    const refreshEntitlements = () => {
      v2_fetch_explore_catalog()
        .then((res) => {
          if (!res?.status || !res?.result?.modules?.length) return;
          try {
            sessionStorage.setItem(
              CACHE_KEY,
              JSON.stringify({ at: Date.now(), modules: res.result.modules })
            );
          } catch {
            /* ignore */
          }
          applyCatalog(res.result.modules);
        })
        .catch(() => {
          /* keep current */
        });
    };

    refreshEntitlements();
    window.addEventListener(ENTITLEMENTS_EVENT, refreshEntitlements);
    return () => window.removeEventListener(ENTITLEMENTS_EVENT, refreshEntitlements);
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith("/access-control")) {
      setOpenRightItem("Roles & Access");
    } else if (location.pathname.startsWith("/settings/support")) {
      setOpenRightItem("Support");
    } else if (
      location.pathname.startsWith("/user")
      || location.pathname.startsWith("/settings/users/crew")
    ) {
      setOpenRightItem("Users");
    }
  }, [location.pathname, activeMenu]);

  /* ======================================================
       🔹 Permission Helpers (SAFE)
    ====================================================== */

  const hasAnyAccess = (rules = []) => {
    if (permissionsLoading || adminPerm.canView) return true;
    const entries = Object.entries(permissions || {});
    return rules.some((rule) =>
      entries.some(([screenKey, access]) => {
        const matches = rule.endsWith(".")
          ? screenKey.startsWith(rule)
          : screenKey === rule;
        if (!matches) return false;
        return ["view", "add", "edit", "delete", "process"].some(
          (field) => access?.[field] === true
        );
      })
    );
  };

  const canShowSidebarItem = (name) => {
    const areaRules = SIDEBAR_ACCESS_AREA_RULES[name];
    if (
      userRestricted &&
      (!areaRules || !areaRules.some((area) => allowedAccessAreas.has(area)))
    ) {
      return false;
    }
    const rules = SIDEBAR_PERMISSION_RULES[name];
    return !rules || hasAnyAccess(rules);
  };

  /**
   * Super Admin is read-only: strip out any create/edit submenu children
   * ("New Entry", "New / Edit", etc.) and retarget each parent to its
   * History / List route so the nav doesn't lead into a creation form.
   */
  const isReadOnlyAdmin = adminPerm.canView;
  /** Master fills nothing in MARPOL — Approvals / History only (same strip as admin create). */
  const hideMarpolCreate = isReadOnlyAdmin || isMasterProfile;

  const isCreateNavItem = (name) => {
    const label = String(name || "").trim().toLowerCase();
    return (
      label === "new entry" ||
      label === "new / edit" ||
      label === "new/edit" ||
      label.startsWith("new ")
    );
  };

  const stripCreateItems = (submenu) => {
    if (!Array.isArray(submenu)) return submenu;
    return submenu
      .map((item) => {
        if (!item) return item;
        const children = Array.isArray(item.children)
          ? item.children.filter((c) => c && !isCreateNavItem(c.name))
          : item.children;

        // Prefer Approvals (Master queue), then List/History when retargeting away from a create URL.
        const approvalsChild = children?.find(
          (c) => c?.url && String(c.name).toLowerCase() === "approvals"
        );
        const listChild = children?.find(
          (c) =>
            c?.url &&
            (String(c.name).toLowerCase() === "list" ||
              String(c.name).toLowerCase() === "history")
        );
        const fallbackUrl = approvalsChild?.url || listChild?.url || children?.[0]?.url || item.url;

        return {
          ...item,
          url: fallbackUrl,
          children,
        };
      })
      .filter((item) => {
        if (!item) return false;
        // Drop submenu items that *only* offered a create/edit shortcut.
        if (isCreateNavItem(item.name)) return false;
        return true;
      });
  };
  useEffect(() => {
    document.body.classList.add("sidebar-enable", "vertical-collpsed");
    const syncSidebarToViewport = () => {
      const storedCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
      // A 200–228px expanded navigation leaves too little usable space on
      // phones/tablets. Start those viewports on the persistent icon rail;
      // the header toggle can still open the full navigation on demand.
      document.body.classList.toggle(
        "sidebar-collapsed",
        window.innerWidth <= 1100 || storedCollapsed
      );
    };
    syncSidebarToViewport();
    window.addEventListener("resize", syncSidebarToViewport);
    localStorage.removeItem("activeMenu");
    return () => window.removeEventListener("resize", syncSidebarToViewport);
  }, []);

  const closeSubmenuFlyout = () => {
    document.getElementById("layout-wrapper")?.classList.remove("active");
  };

  const openSubmenuFlyout = () => {
    document.getElementById("layout-wrapper")?.classList.add("active");
  };

  const getOpenSubmenuParent = (items, menuName = activeMenu, pendingName = pendingFlyout) =>
    items.find((m) => m.submenu && (m.name === menuName || m.name === pendingName));

  // ✅ Left sidebar items
  const getMenuItems = () => {
    // Do not paint inherited/admin navigation while the effective per-user
    // access boundary is still loading after sign-in.
    if (permissionsLoading) return [];

    // Vendor: a single dedicated pricing dashboard — no other module or page.
    if (isVendorProfile) {
      return [
        {
          name: "Vendor Dashboard",
          Icon: CurrencyDollar,
          url: constant.Authedcomponent.vendorDashboard.url,
        },
      ].filter((item) => canShowSidebarItem(item.name));
    }

    // Shipyard: a single dedicated RFQ/quote dashboard — no other module or page.
    if (isShipyardProfile) {
      return [
        {
          name: "Shipyard Dashboard",
          Icon: Anchor,
          url: constant.Authedcomponent.shipyardDashboard.url,
        },
      ].filter((item) => canShowSidebarItem(item.name));
    }

    const items = [
      {
        name: "Dashboard",
        Icon: SquaresFour,
        url: constant.Authedcomponent.dashboard.url,
      },
      {
        name: "Fleet Map",
        Icon: MapTrifold,
        url: constant.Authedcomponent.fleetMap.url,
      },


    ];

    // Top horizontal tabs switch Vessel / FPCT / Other — no Reports flyout submenu.
    if (adminPerm.canView) {
      items.push({
        name: "Reports",
        Icon: PresentationChart,
        url: constant.Authedcomponent.adminVesselReports.url,
      });
    }

    if (isCrewProfile) {
      items.push({
        name: "My Reports",
        Icon: ClipboardText,
        url: constant.Authedcomponent.myDailyReports.url,
      });
    }
    // ---------- MARPOL submenu (defined as data so we can strip
    // "New Entry" children for Super Admin) ----------
    const approvalChild = (url) =>
      !isReadOnlyAdmin &&
      (
        masterSignPerm.canView ||
        masterSignPerm.canProcess ||
        ceSignPerm.canView ||
        ceSignPerm.canProcess ||
        oowSignPerm.canView ||
        oowSignPerm.canProcess
      )
        ? [{ name: "Approvals", url }]
        : [];
    const marpolChildren = (permission, newEntryUrl, approvalsUrl, historyUrl) => [
      ...(permission.canAdd ? [{ name: "New Entry", url: newEntryUrl }] : []),
      ...(permission.canView ? approvalChild(approvalsUrl) : []),
      ...(permission.canView ? [{ name: "History", url: historyUrl }] : []),
    ];
    const marpolSubmenu = [
      {
        name: "Oil Record Part I",
        url: constant.Authedcomponent.marpolOrbPart1History.url,
        children: marpolChildren(
          orb1Perm,
          constant.Authedcomponent.marpolOrbPart1NewEntry.url,
          constant.Authedcomponent.marpolOrbPart1Approvals.url,
          constant.Authedcomponent.marpolOrbPart1History.url
        ),
      },
      {
        name: "Oil Record Part II",
        url: constant.Authedcomponent.marpolOrbPart2History.url,
        children: marpolChildren(
          orb2Perm,
          constant.Authedcomponent.marpolOrbPart2NewEntry.url,
          constant.Authedcomponent.marpolOrbPart2Approvals.url,
          constant.Authedcomponent.marpolOrbPart2History.url
        ),
      },
      {
        name: "Garbage Record Book",
        url: constant.Authedcomponent.marpolGarbageHistory.url,
        children: marpolChildren(
          grbPerm,
          constant.Authedcomponent.marpolGarbageNewEntry.url,
          constant.Authedcomponent.marpolGarbageApprovals.url,
          constant.Authedcomponent.marpolGarbageHistory.url
        ),
      },
      {
        name: "Ballast Water Record",
        url: constant.Authedcomponent.marpolBallastHistory.url,
        children: marpolChildren(
          bwrbPerm,
          constant.Authedcomponent.marpolBallastNewEntry.url,
          constant.Authedcomponent.marpolBallastApprovals.url,
          constant.Authedcomponent.marpolBallastHistory.url
        ),
      },
      {
        name: "Biofouling Record Book",
        url: constant.Authedcomponent.marpolBiofoulingHistory.url,
        children: marpolChildren(
          bfrbPerm,
          constant.Authedcomponent.marpolBiofoulingNewEntry.url,
          constant.Authedcomponent.marpolBiofoulingApprovals.url,
          constant.Authedcomponent.marpolBiofoulingHistory.url
        ),
      },
      {
        name: "Annex IV Record",
        url: constant.Authedcomponent.marpolAnnexIVHistory.url,
        children: marpolChildren(
          annex4Perm,
          constant.Authedcomponent.marpolAnnexIVNewEntry.url,
          constant.Authedcomponent.marpolAnnexIVApprovals.url,
          constant.Authedcomponent.marpolAnnexIVHistory.url
        ),
      },
      {
        name: "Annex VI Record",
        url: constant.Authedcomponent.marpolAnnexVIHistory.url,
        children: marpolChildren(
          annex6Perm,
          constant.Authedcomponent.marpolAnnexVINewEntry.url,
          constant.Authedcomponent.marpolAnnexVIApprovals.url,
          constant.Authedcomponent.marpolAnnexVIHistory.url
        ),
      },
      {
        name: "MARPOL Master Data",
        url: "/marpol-master",
      },
    ].filter((item) =>
      item.name === "MARPOL Master Data"
        ? hasAnyAccess(["masterdata.compliance-templates"])
        : Array.isArray(item.children) && item.children.length > 0
    );

    items.push(
      {
        name: "iShip Intelligence",
        Icon: Sparkle,
        url: constant.Authedcomponent.intelligenceWorkspace.url,
      },
      {
        name: "KPI",
        Icon: ChartLineUp,
        url: constant.Authedcomponent.kpiDashboard.url,
      },
      {
        name: "Vessel Performance",
        Icon: PresentationChart,
        url: constant.Authedcomponent.vesselPerformance.url,
      },
      {
        name: "Environment",
        Icon: Leaf,
        url: constant.Authedcomponent.environment.url,
      },
    );

    if (marpolSubmenu.length > 0) {
      items.push({
        name: "MARPOL",
        Icon: DropHalf,
        url: marpolSubmenu[0]?.url,
        navigateOnParentClick: true,
        submenu: hideMarpolCreate ? stripCreateItems(marpolSubmenu) : marpolSubmenu,
      });
    }

    if (isCrewProfile) {
      const elogsVesselId = vessel?.vessel_id ?? getStoredVesselId();
      items.push({
        name: "Reporting",
        Icon: Boat,
        url: constant.Authedcomponent.vesselDashboard.url,
        ...(elogsVesselId ? { state: { vessel_id: elogsVesselId } } : {}),
      });
    }

    // Logbooks use the page-level tab strip for individual books. The sidebar
    // keeps a single entry point so those same destinations are not duplicated.
    items.push({
      name: "Logs",
      Icon: Notebook,
      url: constant.Authedcomponent.engineLogList.url,
    });

    // Voyage: Master/Captain — port reports; Super Admin — route plan editor.
    const voyageNavState = vessel?.vessel_id
      ? { data: vessel, vessel_id: vessel.vessel_id }
      : undefined;

    if (isMasterProfile) {
      items.push({
        name: "Voyage",
        Icon: Compass,
        url: constant.Authedcomponent.voyageRoutePlan.url,
        state: voyageNavState,
        submenu: [
          {
            name: "Route Plan",
            url: constant.Authedcomponent.voyageRoutePlan.url,
            state: voyageNavState,
          },
          {
            name: "Port Report (Departure)",
            url: constant.Authedcomponent.startVoyage.url,
            state: voyageNavState,
          },
          {
            name: "SOSP Report",
            url: constant.Authedcomponent.sospReportCreate.url,
            state: voyageNavState,
          },
          {
            name: "EOSP Report",
            url: constant.Authedcomponent.eospReportCreate.url,
            state: voyageNavState,
          },
          {
            name: "Port Report (Arrival)",
            url: constant.Authedcomponent.portArrivalReportCreate.url,
            state: voyageNavState,
          },
        ],
      });
    } else if (isReadOnlyAdmin) {
      items.push({
        name: "Voyage",
        Icon: Compass,
        url: constant.Authedcomponent.voyageRoutePlan.url,
      });
    }

    /* Master Data — hub only (vessels, owners, crew live inside the hub) */
    items.push({
      name: "Master Data",
      Icon: Database,
      url: constant.Authedcomponent.masterDataHub.url,
    });

    items.push({
      name: "Template Builders",
      Icon: Blueprint,
      url: constant.Authedcomponent.templateBuildersHub.url,
    });

    /* Modules — flyout only used when icon-rail is collapsed */
    items.push({
      name: "Modules",
      Icon: SquaresFour,
      url: modulePath(
        (entitledModules[0] || ISHIP_MODULES[0]).id,
        (entitledModules[0] || ISHIP_MODULES[0]).submodules?.[0]?.id
      ),
      submenu: buildModulesFlyoutSubmenu(entitledModules),
      modulesFlyout: true,
    });

    items.push({
      name: "Explore",
      Icon: MagnifyingGlass,
      url: constant.Authedcomponent.exploreServices.url,
    });

    const isAdminProfile = adminPerm.canView;

    if (isCrewProfile) {
      items.push({
        name: "Support",
        Icon: Lifebuoy,
        url: constant.Authedcomponent.ticketList.url,
        submenu: [
          { name: "Ticket List", url: constant.Authedcomponent.ticketList.url },
          { name: "Create Ticket", url: constant.Authedcomponent.createTicket.url },
        ],
      });
    }

    // Settings hub: always available to admins; also surfaced to anyone
    // (including crew) explicitly granted a settings.* permission (e.g.
    // settings.sso) via Access Control, so that granular grant isn't a dead
    // toggle with no matching entry point.
    if (isAdminProfile || ssoPerm.canView) {
      items.push({
        name: "Settings",
        Icon: GearSix,
        url: constant.Authedcomponent.settingsHub.url,
      });
    }

    if (adminPerm.canView || ownerPerm.canView || crewPerm.canView) {
      items.push({
        name: "Activity & IP Log",
        Icon: ClockCounterClockwise,
        url: constant.Authedcomponent.adminActivityLog.url,
      });
    }

    return items.filter((item) => canShowSidebarItem(item.name));
  };

  const menuItems = React.useMemo(
    () => getMenuItems(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vessel, permissions, permissionsLoading, roleName, user, userRestricted, accessAreas, adminPerm.canView, ownerPerm.canView, crewPerm.canView, ssoPerm.canView, ceSignPerm.canView, ceSignPerm.canProcess, oowSignPerm.canView, oowSignPerm.canProcess, isCrewProfile, isMasterProfile, entitledModules]
  );

  const railGroups = React.useMemo(() => buildRailGroups(menuItems), [menuItems]);

  const renderNavigationGroupToggle = (groupId, label) => {
    const collapsed = openNavigationGroup !== groupId;
    return (
      <li className="iship-navigation-group-title">
        <button
          type="button"
          className="iship-navigation-group-toggle"
          onClick={() => toggleNavigationGroup(groupId)}
          aria-expanded={!collapsed}
        >
          <span className="iship-navigation-group-label">{label}</span>
          <CaretDown
            className={`iship-navigation-group-caret${collapsed ? " is-collapsed" : ""}`}
            size={14}
            weight="bold"
            aria-hidden="true"
          />
        </button>
      </li>
    );
  };

  const railTooltip = (name) => RAIL_TOOLTIPS[name] || name;

  const renderRailItem = (item, itemKey, groupCollapsed = false) => {
    const hasSubmenu = !!item.submenu;
    const belongsToRoute = routeBelongsToMenu(item, fullLocation);
    const isFlyoutOpen = hasSubmenu && activeMenu === item.name;
    const hasOpenSubmenuFlyout = menuItems.some(
      (m) => m.submenu && activeMenu === m.name
    );
    const isDirectLink = !hasSubmenu && !!item.url;
    const isDirectActive = isDirectLink && belongsToRoute && !hasOpenSubmenuFlyout;
    const isSubmenuActive =
      hasSubmenu && (isFlyoutOpen || (belongsToRoute && activeMenu === item.name));
    const isCurrent = isDirectActive || isSubmenuActive;
    const IconComp = item.Icon || Circle;
    const visibleName = item.displayName || item.name;
    const tooltip = railTooltip(visibleName);

    return (
      <li
        key={itemKey}
        className={`${isCurrent ? "current" : ""}${item.modulesFlyout ? " iship-rail-modules-flyout" : ""}${groupCollapsed ? " iship-rail-item--group-collapsed" : ""}`}
      >
        <Link
          to={item.url || "#"}
          state={item.state || {}}
          className={`waves-effect iship-rail-tooltip ${isCurrent ? "active" : ""}`}
          data-tooltip={tooltip}
          aria-label={tooltip}
          onClick={(e) => {
            // Expanded sidebar: Modules live inline — don't open second panel
            if (item.modulesFlyout && !isSidebarCollapsed()) {
              e.preventDefault();
              const firstMod = entitledModules[0] || ISHIP_MODULES[0];
              const first = firstMod?.submodules?.[0];
              if (first) navigate(modulePath(firstMod.id, first.id));
              closeSubmenuFlyout();
              return;
            }
            if (hasSubmenu && !item.navigateOnParentClick) e.preventDefault();
            handleMenuClick(item.name, hasSubmenu, item.url);
          }}
        >
          <IconComp
            className="leftIconbar"
            {...RAIL_ICON}
            aria-hidden="true"
          />
          <p>{visibleName}</p>
        </Link>
      </li>
    );
  };

  const renderInlineModuleSections = () => {
    const moduleFor = (moduleId) =>
      entitledModules.find((mod) => mod.id === moduleId)
      || (moduleId === "maintenance"
        ? ISHIP_MODULES.find((mod) => mod.id === moduleId)
        : null);

    const renderStructuredLink = ({
      key,
      label,
      url,
      Icon = SquaresFour,
      aliases = [],
    }) => {
      if (!url) return null;
      const active = isNavLinkActive(url, fullLocation, aliases);
      return (
        <li key={key} className={`iship-rail-module-link${active ? " current" : ""}`}>
          <Link
            to={url}
            className={`waves-effect iship-rail-tooltip ${active ? "active" : ""}`}
            data-tooltip={label}
            aria-label={label}
            onClick={() => closeSubmenuFlyout()}
          >
            <Icon className="leftIconbar iship-module-icon" {...RAIL_ICON} aria-hidden="true" />
            <p>{label}</p>
          </Link>
        </li>
      );
    };

    const moduleLink = (moduleId, submoduleId, label, urlOverride) => {
      const mod = moduleFor(moduleId);
      const sub = mod?.submodules?.find((candidate) => candidate.id === submoduleId);
      if (!mod || !sub) return null;
      const Icon = MODULE_ITEM_ICONS[sub.id] || MODULE_ITEM_ICONS[mod.id] || SquaresFour;
      return renderStructuredLink({
        key: `${moduleId}-${submoduleId}-${label}`,
        label: label || sub.name,
        url: urlOverride || modulePath(moduleId, submoduleId),
        Icon,
      });
    };

    const maintenanceLinks = [
      moduleLink("maintenance", "work-orders", "Work Orders"),
      moduleLink("maintenance", "equipment", "Equipment"),
      moduleLink("maintenance", "plans", "Services"),
      moduleLink("maintenance", "equipment", "Counters", `${modulePath("maintenance", "equipment")}?view=counters`),
      moduleLink("maintenance", "history", "History"),
    ];

    const inventoryLinks = [
      moduleLink("inventory-procurement", "stock", "Inventory"),
      moduleLink("inventory-procurement", "stock", "Location Manager", `${modulePath("inventory-procurement", "stock")}?view=locations`),
      moduleLink("procurement", "requisition", "Requisitions"),
      moduleLink("procurement", "purchase-orders", "Purchase Orders"),
      moduleLink("procurement", "goods-receipt", "Goods Receipt"),
      moduleLink("procurement", "delivery-note", "Landing Advice"),
    ];

    const plannerLinks = [
      moduleLink("crew-operations", "weekly-task-management", "Weekly Planning"),
      moduleLink("crew-operations", "daily-tasks-register", "Daily Tasks Register"),
      renderStructuredLink({
        key: "planner-permits",
        label: "Permits",
        url: constant.Authedcomponent.permitToWork.url,
        Icon: ClipboardText,
      }),
      moduleLink("sms", "library", "Checklists", `${modulePath("sms", "library")}?view=checklists`),
      renderStructuredLink({
        key: "planner-risk-assessments",
        label: "Risk Assessments",
        url: `${constant.Authedcomponent.permitToWork.url}?view=risk-assessments`,
        Icon: ShieldWarning,
      }),
    ];

    const hseqLinks = [
      nearMissPerm.canView
        ? renderStructuredLink({
            key: "hseq-near-miss",
            label: "Near Miss",
            url: constant.Authedcomponent.nearMissDashboard.url,
            Icon: ShieldWarning,
          })
        : null,
      moduleLink("hseq", "incidents", "Incidents"),
      moduleLink("maintenance", "defects", "Defects & Findings"),
      moduleLink("vessel-inspection", "vessel-inspection", "Inspection Reports"),
      moduleLink("hseq", "safety-drills", "Safety Drills"),
      moduleLink("sms", "library", "SMS Library"),
    ];

    const certificatePath = modulePath("maintenance", "certificates");
    const certificateLinks = [
      moduleLink("maintenance", "certificates", "Vessel & Equipment Certificates", `${certificatePath}?view=vessels`),
      renderStructuredLink({
        key: "certificate-required-actions",
        label: "Required Actions",
        url: `${certificatePath}?view=priorities`,
        Icon: WarningCircle,
      }),
    ];

    const drydockLinks = [
      moduleLink("drydock", "drydock-repair-list", "Dry Dock Projects"),
    ];

    const renderGroup = (number, label, links) => {
      const visibleLinks = links.filter(Boolean);
      if (!visibleLinks.length) return null;
      const groupId = `operations-${number}`;
      const collapsed = openNavigationGroup !== groupId;
      return (
        <React.Fragment key={`structured-${number}`}>
          {renderNavigationGroupToggle(groupId, label)}
          {!collapsed && (
            <React.Fragment key={`${groupId}-links`}>
              {visibleLinks}
            </React.Fragment>
          )}
        </React.Fragment>
      );
    };

    return (
      <>
        {renderGroup("06", "Maintenance", maintenanceLinks)}
        {renderGroup("07", "Inventory & Procurement", inventoryLinks)}
        {renderGroup("08", "Planner", plannerLinks)}
        {renderGroup("09", "HSEQ", hseqLinks)}
        {renderGroup("10", "Certificate Management", certificateLinks)}
        {renderGroup("11", "Dry Dock", drydockLinks)}
      </>
    );
  };

  /** Prefix-safe active check so e.g. /ticket does not match /ticket/list when list is a sibling. */
  const pathIsUnderNavUrl = (navUrl, pathOnly) => {
    if (!navUrl || navUrl === "#") return false;
    const base = navUrl.split("?")[0];
    if (pathOnly === base) return true;
    if (!pathOnly.startsWith(`${base}/`)) return false;
    const baseSegs = base.split("/").filter(Boolean);
    // Single segment (e.g. /crew) must not match siblings like /crew/list.
    if (baseSegs.length === 1) return false;
    return true;
  };

  const isKpiRoute = (pathOnly) => pathOnly === "/kpi" || pathOnly.startsWith("/kpi/");

  const isSettingsRoute = (pathOnly) => {
    if (pathOnly === "/settings" || pathOnly.startsWith("/settings/")) return true;
    if (pathOnly === "/access-control" || pathOnly.startsWith("/access-control/")) return true;
    if (pathOnly.startsWith("/user/")) return true;
    if (pathOnly.startsWith("/sea-state/")) return true;
    if (pathOnly.startsWith("/unlocode/")) return true;
    if (pathOnly.startsWith("/vessel/group/")) return true;
    return false;
  };

  const isEnvironmentRoute = (pathOnly) =>
    pathOnly === constant.Authedcomponent.environment.url
    || pathOnly.startsWith(`${constant.Authedcomponent.environment.url}/`);

  const isLogsRoute = (pathOnly) =>
    pathOnly.startsWith("/report/engine-log")
    || pathOnly.startsWith("/report/deck-log")
    || pathOnly.startsWith("/report/noon-report")
    || pathOnly.startsWith("/logs/")
    || pathOnly === constant.Authedcomponent.startVoyage.url
    || pathOnly === constant.Authedcomponent.portArrivalReportCreate.url
    || pathOnly === "/operations/cargo-log";

  const isReportsRoute = (pathOnly) => {
    if (pathOnly.startsWith("/admin/vessel-reports")) return true;
    if (pathOnly === constant.Authedcomponent.createReportList.url) return true;
    if (pathOnly.startsWith(`${constant.Authedcomponent.createReportList.url}/`)) return true;
    if (pathOnly.startsWith("/report/operation/")) return true;
    // Other Reports catalogue create/view pages (exclude Logs noon/deck/engine).
    if (
      pathOnly.startsWith("/report/")
      && !isLogsRoute(pathOnly)
    ) {
      return true;
    }
    return false;
  };

  const menuItemOwnsRoute = (item, pathOnly) => {
    if (item?.name === "KPI" && isKpiRoute(pathOnly)) return true;
    if (item?.name === "Settings" && isSettingsRoute(pathOnly)) return true;
    if (item?.name === "Environment" && isEnvironmentRoute(pathOnly)) return true;
    if (item?.name === "Logs" && isLogsRoute(pathOnly)) return true;
    if (item?.name === "Reports" && isReportsRoute(pathOnly)) return true;
    return item?.url ? pathIsUnderNavUrl(item.url, pathOnly) : false;
  };

  const isNavLinkActive = (url, currentLocation, peerUrls = []) => {
    if (!url || !currentLocation) return false;

    if (url.includes("?")) {
      return currentLocation === url;
    }

    const pathOnly = currentLocation.split("?")[0];
    if (pathOnly === url) return true;
    if (!pathIsUnderNavUrl(url, pathOnly)) return false;

    return !peerUrls.some((peer) => {
      if (!peer || peer === url) return false;
      if (peer.includes("?")) return currentLocation === peer;
      const peerPath = currentLocation.split("?")[0];
      return peerPath === peer || peerPath.startsWith(`${peer}/`);
    });
  };

  /** Leaf submenu row — yields to more specific sibling paths (e.g. FPCT vs Vessel Reports). */
  const isSubmenuLeafActive = (item, currentLocation, siblingUrls = []) => {
    if (!item?.url || item.url === "#") return false;

    if (item.url.includes("?")) {
      return currentLocation === item.url;
    }

    if (currentLocation.includes("?")) {
      const exactQuerySibling = siblingUrls.some(
        (u) => u && u !== item.url && u.includes("?") && u === currentLocation
      );
      if (exactQuerySibling) return false;
    }

    return isNavLinkActive(item.url, currentLocation, siblingUrls);
  };

  const getSubmenuSiblingUrls = (submenu) => {
    const urls = [];
    (submenu || []).forEach((s) => {
      if (s?.url) urls.push(s.url);
      if (Array.isArray(s?.children)) {
        s.children.forEach((c) => {
          if (c?.url) urls.push(c.url);
        });
      }
    });
    return urls;
  };

  const submenuMatchesRoute = (submenu, currentLocation) => {
    const siblingUrls = getSubmenuSiblingUrls(submenu);
    return (submenu || []).some((sub) =>
      routeInSubmenuItem(sub, currentLocation, siblingUrls)
    );
  };

  const routeInSubmenuItem = (item, currentLocation, siblingUrls = []) => {
    if (!item || item.sectionHeader) return false;

    if (Array.isArray(item.children) && item.children.length > 0) {
      const childUrls = item.children.map((c) => c?.url).filter(Boolean);
      const peerUrls = [...new Set([...(siblingUrls || []), ...childUrls])];
      if (
        item.children.some(
          (c) => c?.url && isNavLinkActive(c.url, currentLocation, peerUrls)
        )
      ) {
        return true;
      }
      // Folder parent URL (e.g. cadence list) should also count as a match
      if (item.url && isSubmenuLeafActive(item, currentLocation, peerUrls)) {
        return true;
      }
      return false;
    }

    return isSubmenuLeafActive(item, currentLocation, siblingUrls);
  };

  const findMenuOwnerForRoute = (items, currentLocation, preferredMenuName = null) => {
    const pathOnly = currentLocation.split("?")[0];

    const menuOwnsRoute = (item) => submenuMatchesRoute(item?.submenu, currentLocation);

    if (preferredMenuName) {
      const preferred = items.find((m) => m.name === preferredMenuName);
      if (preferred && menuOwnsRoute(preferred)) {
        return preferredMenuName;
      }
    }

    for (const item of items) {
      if (item.url && pathOnly === item.url) {
        return null;
      }
      if (menuOwnsRoute(item)) {
        return item.name;
      }
    }
    return null;
  };

  const routeBelongsToMenu = (item, currentLocation) => {
    const pathOnly = currentLocation.split("?")[0];
    if (menuItemOwnsRoute(item, pathOnly)) return true;
    return !!submenuMatchesRoute(item.submenu, currentLocation);
  };

  const findDirectMenuItem = (items, pathOnly) =>
    items.find((item) => item.url && !item.submenu && menuItemOwnsRoute(item, pathOnly));

  const fullLocation = (() => {
    const base = `${location.pathname}${location.search}`;
    if (location.pathname === "/access-control" && !location.search) {
      return "/access-control?section=permissions";
    }
    return base;
  })();

  const handleMenuClick = (menuName, hasSubmenu, url) => {
    if (hasSubmenu) {
      const newActive = activeMenu === menuName ? null : menuName;

      if (newActive) {
        menuNavRef.current = newActive;
        setPendingFlyout(newActive);
        openSubmenuFlyout();
        setSearchTerm("");
        setOpenRightItem(null);
        setActiveMenu(newActive);

        // A parent item (for example MARPOL) opens its flyout. Navigation is
        // performed only after the user chooses an actual child page.
        return;
      }

      menuNavRef.current = null;
      setPendingFlyout(null);
      closeSubmenuFlyout();
      setActiveMenu(null);
    } else if (url) {
      menuNavRef.current = null;
      setPendingFlyout(null);
      setActiveMenu(null);
      closeSubmenuFlyout();
      setSearchTerm("");
      setOpenRightItem(null);
    }
  };

  const prevPathRef = React.useRef(location.pathname);

  /* Sync flyout to the current route when the URL changes */
  useEffect(() => {
    const pathOnly = location.pathname;
    const pathChanged = prevPathRef.current !== pathOnly;
    prevPathRef.current = pathOnly;

    if (pathChanged && !menuNavRef.current) {
      setPendingFlyout(null);
    }

    if (findDirectMenuItem(menuItems, pathOnly)) {
      if (pathChanged && !getOpenSubmenuParent(menuItems)) {
        setActiveMenu(null);
        closeSubmenuFlyout();
      }
      return;
    }

    const preferred = menuNavRef.current || pendingFlyout || activeMenu;
    const owner = findMenuOwnerForRoute(menuItems, fullLocation, preferred);
    if (owner) {
      // Expanded: Modules are inline section labels — never open the second panel
      if (owner === "Modules" && !isSidebarCollapsed()) {
        setActiveMenu(null);
        closeSubmenuFlyout();
        if (menuNavRef.current) menuNavRef.current = null;
        return;
      }
      setActiveMenu(owner);
      if (!isNarrowViewport()) setSubmenuDrawerOpen(true);
      if (menuNavRef.current) {
        menuNavRef.current = null;
      }
      return;
    }
    if (pathChanged) {
      setActiveMenu(null);
      closeSubmenuFlyout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  /* Re-sync after menu items load (permissions) */
  useEffect(() => {
    const pathOnly = location.pathname;
    if (findDirectMenuItem(menuItems, pathOnly)) {
      if (!getOpenSubmenuParent(menuItems)) {
        setActiveMenu(null);
        closeSubmenuFlyout();
      }
      return;
    }
    const preferred = menuNavRef.current || pendingFlyout || activeMenu;
    const owner = findMenuOwnerForRoute(menuItems, fullLocation, preferred);
    if (owner) {
      // Expanded: Modules stay inline — never reopen the second panel
      if (owner === "Modules" && !isSidebarCollapsed()) {
        setActiveMenu(null);
        closeSubmenuFlyout();
        if (menuNavRef.current) menuNavRef.current = null;
        return;
      }
      setActiveMenu(owner);
      if (!isNarrowViewport()) setSubmenuDrawerOpen(true);
      if (menuNavRef.current) {
        menuNavRef.current = null;
      }
    } else if (!pendingFlyout && !activeMenu) {
      setActiveMenu(null);
      closeSubmenuFlyout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems]);

  /* User clicked a submenu header (e.g. MARPOL) before picking a page */
  useEffect(() => {
    if (!pendingFlyout) return;
    setActiveMenu(pendingFlyout);
    openSubmenuFlyout();
  }, [pendingFlyout]);

  /* Clear stale submenu search when switching left-rail sections */
  useEffect(() => {
    setSearchTerm("");
  }, [activeMenu]);

  useEffect(() => {
    const submenu = menuItems.find((m) => m.name === activeMenu)?.submenu || [];
    const siblingUrls = getSubmenuSiblingUrls(submenu);
    const match = submenu.find((item) => routeInSubmenuItem(item, fullLocation, siblingUrls));
    if (match) setOpenRightItem(match.name);
    // Route helpers are pure and intentionally scoped with the menu builder.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullLocation, activeMenu, menuItems]);


  // 🔍 Filter submenu items on search.
  // NOTE: `menuItems` is included so the right rail refreshes when
  // permissions load asynchronously (e.g. Super Admin's "New Entry"
  // children are stripped only after the role resolves).
  useEffect(() => {
    const submenu = menuItems.find((m) => m.name === activeMenu)?.submenu || [];
    if (searchTerm.trim() === "") {
      setFilteredSubmenu(submenu);
    } else {
      const q = searchTerm.toLowerCase();
      const filtered = [];
      let lastHeader = null;
      submenu.forEach((s) => {
        if (s.sectionHeader) {
          lastHeader = s;
          return;
        }
        if ((s.name || "").toLowerCase().includes(q)) {
          if (lastHeader && (filtered.length === 0 || filtered[filtered.length - 1] !== lastHeader)) {
            filtered.push(lastHeader);
          }
          filtered.push(s);
        }
      });
      setFilteredSubmenu(filtered);
    }
  }, [searchTerm, activeMenu, menuItems]);

  return (
    <div
      className={`parent-sidebar iship-icon-rail${
        activeMenu === "Modules" ? " iship-modules-flyout-open" : ""
      }`}
    >
      <nav
        className="vertical-menu iship-rail-shell"
        aria-label="Main navigation"
      >
        <div id="sidebar-menu">
          <ul className="iship-rail-list" id="side-menu">
            {railGroups.map((group) => (
              <React.Fragment key={group.id}>
                {!group.hiddenHeader && group.label && (
                  renderNavigationGroupToggle(group.id, group.label)
                )}
                {group.items.map((item, index) => (
                  <React.Fragment key={`${group.id}-${index}`}>
                    {item.modulesFlyout && (
                      renderInlineModuleSections()
                    )}
                    {renderRailItem(
                      item,
                      `${group.id}-${index}`,
                      !group.hiddenHeader && openNavigationGroup !== group.id
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}

            <li className="iship-rail-spacer" aria-hidden="true" />

            <li className="text-danger iship-rail-logout">
              <a
                href={constant.commoncomponent.logout.url}
                className="waves-effect iship-rail-tooltip"
                data-tooltip="Logout"
                aria-label="Logout"
              >
                <SignOut
                  className="leftIconbar"
                  {...RAIL_ICON}
                  aria-hidden="true"
                />
                <p className="text-danger">Logout</p>
              </a>
            </li>
          </ul>
        </div>
      </nav>

      <div
        className={`vertical-right-menu iship-submenu-panel${
          activeMenu === "Modules" ? " iship-submenu-panel--modules" : ""
        }`}
      >
        <div className="vertical-right-inner">
          {activeMenu && (
            <div className="iship-submenu-head">
              <span className="iship-submenu-title">{activeMenu}</span>
            </div>
          )}
          {activeMenu !== "Modules" && (
            <div className="search-section-box">
              <div className="input-group mt-2 mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search submenu..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="input-group-text bg-white border-start-0">
                  <MagnifyingGlass {...MODERN_ICON.sm} aria-hidden="true" />
                </span>
              </div>
            </div>
          )}
          <ul className={activeMenu === "Modules" ? "iship-module-flyout-list" : undefined}>
            {filteredSubmenu.length > 0 ? (
              filteredSubmenu.map((item, i) => {
                if (item.sectionHeader) {
                  return (
                    <li key={`sec-${i}`} className="iship-module-section-label">
                      <span>{item.name}</span>
                    </li>
                  );
                }

                const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                const LeafIcon = MODULE_ITEM_ICONS[item.iconKey] || SquaresFour;
                const isOpen = openRightItem === item.name;
                const siblingUrls = getSubmenuSiblingUrls(filteredSubmenu);
                const childUrls = hasChildren
                  ? item.children.map((c) => c.url).filter(Boolean)
                  : [];
                // Prefer more-specific peers across the whole Logs submenu so
                // duplicate/shared paths don't light up two List items at once.
                const activePeerUrls = [...new Set([...siblingUrls, ...childUrls])];
                const pathOnly = fullLocation.split("?")[0];
                const childActive = hasChildren && item.children.some(
                  (c) => isNavLinkActive(c.url, fullLocation, activePeerUrls)
                );
                const itemPathOnly = (item.url || "").split("?")[0];
                const parentDirectActive =
                  !childActive &&
                  (hasChildren
                    ? item.url?.includes("?")
                      ? fullLocation === item.url
                      : fullLocation === item.url || pathOnly === itemPathOnly
                    : isSubmenuLeafActive(item, fullLocation, siblingUrls));
                const isExpanded = hasChildren && (isOpen || childActive || parentDirectActive);

                return (
                  <li key={i} className={parentDirectActive ? "iship-submenu-active" : ""}>
                    <div
                      className={`right-parent ${isExpanded ? "open" : ""} ${parentDirectActive ? "active" : ""} ${childActive ? "has-active-child" : ""}`}
                      onClick={() =>
                        hasChildren && setOpenRightItem(isExpanded ? null : item.name)
                      }
                    >
                      <Link
                        to={item.url}
                        state={item.state}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isSidebarCollapsed()) closeSubmenuFlyout();
                        }}
                      >
                        {activeMenu === "Modules" && (
                          <LeafIcon className="iship-flyout-icon" {...MODERN_ICON.sm} aria-hidden="true" />
                        )}
                        {item.name}
                      </Link>
                    </div>

                    {hasChildren && isExpanded && (
                      <ul className="right-child">
                        {item.children.map((child, c) => {
                          const childIsActive = isNavLinkActive(
                            child.url,
                            fullLocation,
                            activePeerUrls
                          );
                          return (
                          <li key={c} className={childIsActive ? "iship-submenu-active" : ""}>
                            <Link
                              to={child.url}
                              state={child.state}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSidebarCollapsed()) closeSubmenuFlyout();
                              }}
                              className={childIsActive ? "active" : ""}
                            >
                              {child.name}
                            </Link>
                          </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })
            ) : (
              <li className="text-muted text-center mt-3">
                No matching results
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CommonSidebar;
